import React, { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
	Wrapper,
	PageLead,
	Breakdown,
	Button,
	ContentWrapper,
	UFOIcon,
	Typography,
	ArrowRight,
	Maintenance,
	withLocalization,
	ContentLoader,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import {
	IDowntime,
	ITypeVignettenFehlerseitenFields,
	OfferContext,
	TollService,
	checkForMaintenance,
	convertApiDate,
	getErrorPageCollection,
	getGermanDateFormat,
	getLabelValue,
} from '@adac-mp/shared';
import { useQuery } from '@apollo/client';

const Error = () => {
	const { viewport } = useViewport();
	const { lang, labels } = useContext(OfferContext);
	const { error, data, loading } = useQuery(getErrorPageCollection, {
		variables: { lang: lang || 'de' },
	});

	const errorLabels = !error
		? (data?.vignettenFehlerseitenCollection.items[0] as ITypeVignettenFehlerseitenFields)
		: undefined;
	const [errorType, setErrorType] = useState<string | null>(null);
	const [downtimes, setDowntimes] = useState<IDowntime[] | []>([]);
	const renderCTAButton =
		!errorType || errorType === 'payment' || errorType === 'purchase' || errorType === 'confirmation';

	useEffect(() => {
		const maintenanceCheck = () => {
			TollService.getAvail({
				body: {
					module: 'TOLL',
					fuelTypeId: -1,
					moduleDetailId: 1,
					moduleTypeId: 1,
					suppliers: ['ASFINAG'],
				},
				lang,
			}).then(({ offers }) => {
				const isMaintenanceOngoing = checkForMaintenance(offers);
				if (isMaintenanceOngoing) {
					setDowntimes(offers[0]?.downtimes);
				} else {
					window.location.replace(
						'https://www.adac.de/fahrzeugwelt/maut-vignette/buchung/oesterreich/onboarding/',
					);
				}
			});
		};

		const urlParams = new URLSearchParams(window?.location.search);
		const urlVariant = urlParams.get('type');
		setErrorType(urlVariant);
		if (urlVariant === 'maintenance') {
			maintenanceCheck();
		}
	}, []);

	const getErrorIcon = () => {
		switch (errorType) {
			case 'api':
				return <UFOIcon />;

			case 'maintenance':
				return <Maintenance />;

			default:
				return <Breakdown />;
		}
	};

	const getErrorHeadline = () => {
		switch (errorType) {
			case 'api':
				return errorLabels?.apiErrorTitle || 'Wir sind bald wieder da!';
			case 'maintenance':
				return errorLabels?.maintenanceTitle || 'Es wird geschraubt!';
			case 'payment':
				return errorLabels?.paymentErrorTitle || 'PAYMENT ERROR TITLE';
			case 'purchase':
				return errorLabels?.purchasetErrorTitle || 'PURCHASE ERROR TITLE';
			case 'confirmation':
				return errorLabels?.confirmationErrorTitle || 'CONFIRMATION ERROR TITLE';
			default:
				return errorLabels?.errorPageTitle || 'Zeit für die gelben Engel!';
		}
	};

	const renderDowtimes = () => {
		const downtime = downtimes[0];
		if (!downtime) return <Skeleton width="100%" height="100%" />;

		const dateRangesAreEqual =
			downtime.usageFrom && downtime.usageTo ? downtime.usageFrom.date === downtime.usageTo.date : false;

		const from = getGermanDateFormat(convertApiDate(downtime?.usageFrom), true, lang, dateRangesAreEqual, labels);
		const to = getGermanDateFormat(convertApiDate(downtime?.usageTo), true, lang, dateRangesAreEqual, labels);
		return viewport === 'mobile' ? (
			<span>
				{from} — <br /> {to}
			</span>
		) : (
			<span>
				{from} — {to}
			</span>
		);
	};

	const getErrorDescription = () => {
		switch (errorType) {
			case 'api':
				return (
					errorLabels?.apiErrorText ||
					'Das Mautportal ist vorübergehend nicht erreichbar. Versuchen Sie es später erneut.'
				);
			case 'maintenance':
				return (
					<span className="w-full">
						{errorLabels?.maintenanceText ||
							'Aufgrund von Wartungsarbeiten ist das Mautportal während des folgenden Zeitraums nicht erreichbar:'}
						{/* Times to be provided by the BE or Contentful */}
						<Typography
							variant="title"
							color="title"
							size="xl"
							className="w-full block text-center pt-3 md:whitespace-nowrap">
							{renderDowtimes()}
						</Typography>
					</span>
				);
			case 'payment':
				return errorLabels?.paymentErrorText || 'PAYMENT ERROR TEXT';
			case 'purchase':
				return errorLabels?.purchasetErrorText || 'PURCHASE ERROR TEXT';
			case 'confirmation':
				return errorLabels?.confirmationErrorText || 'CONFIRMATION ERROR TEXT';
			default:
				return errorLabels?.errorPageText || 'Auch wir sind nicht vor Pannen sicher. Versuchen Sie es erneut.';
		}
	};

	const renderAdacMapsLink = () => (
		<Button
			href={errorLabels?.planRouteLink || 'https://maps.adac.de/'}
			variant="tertiary"
			className="mt-4 w-fit self-center gap-1.5 group">
			<ArrowRight fill="currentColor" size="20" />
			{errorLabels?.planRouteLabel || 'Jetzt Route planen'}
		</Button>
	);

	const renderErrorApiContent = () => (
		<div className="flex flex-col px-3.5 text-center md:px-5.5">
			<Typography color="lightText">
				{errorLabels?.apiErrorSubText ||
					'Planen Sie in der Zwischenzeit Ihre Route mit ADAC Maps und erfahren Sie alle wichtigen Infos zu Ihrer Reise.'}
			</Typography>
			{renderAdacMapsLink()}
		</div>
	);

	const renderErrorMaintenanceContent = () => (
		<div className="flex flex-col px-3.5 text-center md:px-5.5">
			<Typography color="lightText">
				{errorLabels?.maintenanceSubtitle ||
					'Planen Sie in der Zwischenzeit Ihre Route mit ADAC Maps und erfahren Sie alle wichtigen Infos zu Ihrer Reise.'}
			</Typography>
			{renderAdacMapsLink()}
		</div>
	);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<Wrapper className="bg-white items-center h-[calc(100vh_-_56px)] md:h-[calc(100vh_-_98px)]">
					<ContentWrapper>
						<PageLead
							icon={getErrorIcon()}
							headline={getErrorHeadline()}
							description={getErrorDescription()}
						/>
						{renderCTAButton && (
							<Button
								fullWidth
								href="https://www.adac.de/fahrzeugwelt/maut-vignette"
								className="mb-5 self-center md:max-w-[292px]">
								{getLabelValue('mp.backToStart', labels) || 'Zurück zur Startseite'}
							</Button>
						)}
						{errorType === 'api' && renderErrorApiContent()}
						{errorType === 'maintenance' && renderErrorMaintenanceContent()}
					</ContentWrapper>
				</Wrapper>
			)}
		</>
	);
};

export const ErrorPage = withLocalization(Error);
